// hooks/useOattCustomer.tsx

import { useEffect } from 'react';
import { Select, Spin } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_OATT_CUSTOMERS } from '@graphql/oattcustomers.gql';
import { useOattSession } from '@hooks/useOattSession';

interface OattCustomer {
  customer_id: number;
  customer_code: string;
}

interface UseOattCustomerReturn {
  customerId: number | null;
  setCustomerId: (id: number | null) => void;
  customerCode: string;
  customers: OattCustomer[];
  loadingCustomers: boolean;
  CustomerSelect: React.FC<{ className?: string; style?: React.CSSProperties }>;
}

export function useOattCustomer(): UseOattCustomerReturn {
  const { customerId, setCustomerId } = useOattSession();

  const { data, loading: loadingCustomers } = useQuery<{
    oattCustomers: OattCustomer[];
  }>(GET_OATT_CUSTOMERS, { fetchPolicy: 'cache-first' });

  const customers = data?.oattCustomers ?? [];

  // Auto-select first customer only when no customer is selected yet
  useEffect(() => {
    if (customers.length > 0 && customerId === null) {
      setCustomerId(customers[0].customer_id);
    }
  }, [customers, customerId]);

  const customerCode =
    customers.find((c) => c.customer_id === customerId)?.customer_code ?? '';

  const CustomerSelect: React.FC<{
    className?: string;
    style?: React.CSSProperties;
  }> = ({ className, style }) => {
    if (loadingCustomers) return <Spin size="small" data-testid="customer-select-loading" />;
    if (customers.length === 0) return null;

    return (
      <Select
        value={customerId ?? undefined}
        onChange={(val) => setCustomerId(Number(val))}
        className={className}
        style={{ minWidth: 180, ...style }}
        popupMatchSelectWidth={false}
        size="small"
        data-testid="customer-select"
        data-value={customerCode}
        options={customers.map((c) => ({
          value: c.customer_id,
          label: c.customer_code,
          'data-testid': 'customer-option',
          'data-value': c.customer_code,
        }))}
      />
    );
  };

  return {
    customerId,
    setCustomerId,
    customerCode,
    customers,
    loadingCustomers,
    CustomerSelect,
  };
}
